import { Component, Project } from "projen";
import { NodeProject } from "projen/lib/javascript";
import {
  ProjectMetadataOptions,
  ResolvedProjectMetadata,
} from "./project-metadata-options";
import { MONOREPO_LAYOUT_SEED_BLOCK } from "../agent/bundles/project-context";

/**
 * Regex patterns for extracting owner and name from GitHub repository URLs.
 *
 * Matches:
 * - `https://github.com/owner/repo`
 * - `https://github.com/owner/repo.git`
 * - `git@github.com:owner/repo.git`
 * - `git+https://github.com/owner/repo.git`
 */
const GITHUB_HTTPS_RE =
  /(?:https?:\/\/|git\+https:\/\/)github\.com\/([^/]+)\/([^/.]+)(?:\.git)?/;
const GITHUB_SSH_RE = /git@github\.com:([^/]+)\/([^/.]+)(?:\.git)?/;

/**
 * Extracts owner and name from a GitHub repository URL.
 * Returns undefined values if the URL does not match expected formats.
 */
function parseGitHubUrl(url: string): {
  owner: string | undefined;
  name: string | undefined;
} {
  const httpsMatch = url.match(GITHUB_HTTPS_RE);
  if (httpsMatch) {
    return { owner: httpsMatch[1], name: httpsMatch[2] };
  }

  const sshMatch = url.match(GITHUB_SSH_RE);
  if (sshMatch) {
    return { owner: sshMatch[1], name: sshMatch[2] };
  }

  return { owner: undefined, name: undefined };
}

/**
 * Provides structured project metadata consumed by AgentConfig, skills,
 * and other configulator features at synthesis time.
 *
 * This is a project-level component — it describes the project itself,
 * not any specific feature. It lives alongside MonorepoProject and
 * TypeScriptProject and is discovered by consumers via the static
 * `.of()` factory method.
 */
export class ProjectMetadata extends Component {
  /**
   * Returns the ProjectMetadata instance for a project. Walks up the parent
   * chain so sub-projects resolve the metadata declared on a root
   * `MonorepoProject` without needing a duplicate declaration of their own.
   * Returns `undefined` if no ancestor has a `ProjectMetadata` component.
   */
  public static of(project: Project): ProjectMetadata | undefined {
    const isProjectMetadata = (c: Component): c is ProjectMetadata =>
      c instanceof ProjectMetadata;
    let current: Project | undefined = project;
    while (current) {
      const found = current.components.find(isProjectMetadata);
      if (found) {
        return found;
      }
      current = current.parent;
    }
    return undefined;
  }

  /** Resolved metadata with auto-detected values filled in. */
  public readonly metadata: ResolvedProjectMetadata;

  constructor(project: Project, options: ProjectMetadataOptions = {}) {
    super(project);
    this.metadata = this.resolveMetadata(options);
  }

  /**
   * Merges explicit options with auto-detected values.
   * Auto-detection reads the repository URL from package.json
   * (via Projen's NodePackage manifest) and parses GitHub owner/name.
   * Explicit options always take precedence over auto-detected values.
   */
  private resolveMetadata(
    options: ProjectMetadataOptions,
  ): ResolvedProjectMetadata {
    const autoDetected = this.autoDetectRepository();

    return {
      repository: {
        owner: options.repository?.owner ?? autoDetected.owner,
        name: options.repository?.name ?? autoDetected.name,
        defaultBranch: options.repository?.defaultBranch ?? "main",
      },
      githubProject: options.githubProject,
      organization: options.organization,
      slack: options.slack,
      labels: options.labels,
      milestones: options.milestones,
      docsPath: options.docsPath,
      deployment: options.deployment,
      monorepoLayoutSeedBlock: this.resolveMonorepoLayoutSeedBlock(),
    };
  }

  /**
   * Return the maintainer-seed addendum for the `project-context`
   * bundle, or an empty string when the root project either has no
   * layout-enforcement opinion or has opted out
   * (`layoutEnforcement: "off"`).
   *
   * Uses duck-typing on the root project's `layoutEnforcement`
   * property to avoid a circular import with `MonorepoProject`. The
   * concrete `MonorepoProject` class exposes this property as a
   * string; anything else resolves to the empty string so repos that
   * predate the monorepo layout contract see no change.
   */
  private resolveMonorepoLayoutSeedBlock(): string {
    const root: unknown = this.project.root;
    const enforcement = (root as { readonly layoutEnforcement?: unknown })
      .layoutEnforcement;
    if (typeof enforcement !== "string" || enforcement === "off") {
      return "";
    }
    return MONOREPO_LAYOUT_SEED_BLOCK;
  }

  /**
   * Attempts to auto-detect repository owner and name from the Projen
   * project's package.json repository field.
   */
  private autoDetectRepository(): {
    owner: string | undefined;
    name: string | undefined;
  } {
    if (!(this.project instanceof NodeProject)) {
      return { owner: undefined, name: undefined };
    }

    const manifest = this.project.package.manifest;
    const repoField = manifest.repository;

    if (!repoField) {
      return { owner: undefined, name: undefined };
    }

    // repository can be a string URL or an object { type, url }
    const url =
      typeof repoField === "string" ? repoField : (repoField.url ?? "");

    return parseGitHubUrl(url);
  }
}
